import { useMemo, useState } from "react";
import { Gauge, Search } from "lucide-react";
import { api, errMsg } from "../lib/api";
import { Button } from "./ui/button";
import { Checkbox } from "./ui/checkbox";
import { Dialog, DialogContent, DialogDescription, DialogHeader, DialogTitle } from "./ui/dialog";
import { Input } from "./ui/input";
import { Label } from "./ui/label";

const PRESETS = [50, 90, 100, 150, 200];

export function BulkBandwidthCapDialog({ open, routers, onClose }) {
  const [q, setQ] = useState("");
  const [sel, setSel] = useState(new Set());
  const [mbps, setMbps] = useState("90");
  const [wan, setWan] = useState("ether1");
  const [prog, setProg] = useState(null);

  const list = useMemo(() => routers.filter((r) => (r.name || "").toLowerCase().includes(q.toLowerCase())), [routers, q]);
  const toggle = (id) => setSel((p) => { const n = new Set(p); if (n.has(id)) n.delete(id); else n.add(id); return n; });
  const allShown = list.length > 0 && list.every((r) => sel.has(r.id));

  const run = async () => {
    const targets = routers.filter((r) => sel.has(r.id));
    setProg({ done: 0, total: targets.length, ok: 0, fail: [] });
    for (const r of targets) {
      try {
        await api.put(`/bandwidth-cap/${r.id}`, { mbps: Number(mbps), wan: wan.trim() });
        setProg((p) => ({ ...p, done: p.done + 1, ok: p.ok + 1 }));
      } catch (e) {
        setProg((p) => ({ ...p, done: p.done + 1, fail: [...p.fail, `${r.name}: ${errMsg(e)}`] }));
      }
    }
  };
  const finished = prog && prog.done >= prog.total;
  const close = () => { if (prog && !finished) return; const changed = !!prog; setProg(null); setSel(new Set()); onClose(changed); };

  return (
    <Dialog open={open} onOpenChange={close}>
      <DialogContent className="max-w-lg" data-testid="dialog-bulk-cap">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2"><Gauge className="w-5 h-5 text-amber-400" /> Batas Bandwidth Massal</DialogTitle>
          <DialogDescription>Pilih beberapa cabang, lalu terapkan batas yang sama ke semua router dalam satu klik.</DialogDescription>
        </DialogHeader>
        {!prog ? (
          <div className="space-y-3">
            <div className="grid grid-cols-2 gap-3">
              <div className="space-y-1.5">
                <Label>Bandwidth (Mbps)</Label>
                <Input type="number" min="1" value={mbps} onChange={(e) => setMbps(e.target.value)} data-testid="input-bulk-cap-mbps" />
                <div className="flex flex-wrap gap-1">
                  {PRESETS.map((p) => <button key={p} type="button" onClick={() => setMbps(String(p))} className={`px-2 py-0.5 rounded-full text-[11px] border ${String(p) === mbps ? "bg-amber-500 text-black border-amber-500" : "border-border hover:bg-accent"}`} data-testid={`bulk-cap-preset-${p}`}>{p}</button>)}
                </div>
              </div>
              <div className="space-y-1.5">
                <Label>Interface WAN (sama di semua cabang)</Label>
                <Input value={wan} onChange={(e) => setWan(e.target.value)} placeholder="ether1" data-testid="input-bulk-cap-wan" />
                <div className="text-[11px] text-muted-foreground">Nama interface ke Starlink/ISP, mis. ether1</div>
              </div>
            </div>
            <div className="relative">
              <Search className="w-4 h-4 absolute left-2.5 top-3 text-muted-foreground" />
              <Input className="pl-8" placeholder="Cari cabang…" value={q} onChange={(e) => setQ(e.target.value)} data-testid="input-bulk-cap-search" />
            </div>
            <div className="flex items-center justify-between text-xs">
              <label className="flex items-center gap-2 cursor-pointer"><Checkbox checked={allShown} onCheckedChange={(c) => setSel((p) => { const n = new Set(p); list.forEach((r) => (c ? n.add(r.id) : n.delete(r.id))); return n; })} data-testid="bulk-cap-select-all" /> Pilih semua yang tampil ({list.length})</label>
              <span className="font-semibold" data-testid="bulk-cap-selected-count">{sel.size} cabang dipilih</span>
            </div>
            <div className="max-h-64 overflow-y-auto rounded-md border border-border divide-y divide-border" data-testid="bulk-cap-list">
              {list.map((r) => (
                <label key={r.id} className="flex items-center gap-2 px-2.5 py-1.5 text-sm cursor-pointer hover:bg-accent/50">
                  <Checkbox checked={sel.has(r.id)} onCheckedChange={() => toggle(r.id)} data-testid={`bulk-cap-row-${r.name}`} />
                  <span className="flex-1 truncate">{r.name}</span>
                  {r.bandwidth_cap?.mbps && <span className="text-[11px] font-mono text-amber-400">{r.bandwidth_cap.mbps} Mbps</span>}
                </label>
              ))}
            </div>
            <Button type="button" className="w-full bg-amber-500 hover:bg-amber-600 text-black" disabled={!sel.size || !mbps || Number(mbps) < 1 || !wan.trim()} onClick={run} data-testid="btn-bulk-cap-run">
              Terapkan {mbps} Mbps ke {sel.size} Cabang
            </Button>
          </div>
        ) : (
          <div className="space-y-3" data-testid="bulk-cap-progress">
            <div className="text-sm">Proses {prog.done}/{prog.total} — berhasil <b className="text-emerald-400">{prog.ok}</b>, gagal <b className="text-rose-400">{prog.fail.length}</b></div>
            <div className="h-2 rounded bg-accent overflow-hidden"><div className="h-full bg-amber-500 transition-[width]" style={{ width: `${(prog.done / Math.max(1, prog.total)) * 100}%` }} /></div>
            {prog.fail.length > 0 && <div className="max-h-40 overflow-y-auto rounded-md border border-rose-500/30 bg-rose-500/5 text-xs p-2 space-y-1">{prog.fail.map((f) => <div key={f}>{f}</div>)}</div>}
            {finished && <Button type="button" variant="outline" className="w-full" onClick={close} data-testid="btn-bulk-cap-close">Tutup</Button>}
          </div>
        )}
      </DialogContent>
    </Dialog>
  );
}
